import { FormControlLabel, makeStyles, styled, Switch, withStyles } from "@material-ui/core";
import { attributes, type } from "@type-r/models";
import { useLinked, useModel } from '@type-r/react';
import { Column, Columns, div, Flex, Input } from "lqx-react";
import React, { useEffect, useState } from "react";
import { AssetFilterCriteria } from "../../api/assetFilterCriteria";
import { AssetType } from "../../api/assetType";
import { getCompanies } from "../../api/company";
import { DigitizationSelect } from "../../components/select";
import {
  CancelButton, ConfirmationButton, ErrorHighlighter, RightAlignedButtons, StartOverButton, WorkflowBody, WorkflowFooter, WorkflowTitle
} from "../../shared";
import { SwitchButton } from "../common/switch-button";
import { ImportTemplateDetails } from "../models/import-template-details";
import { globalStore } from "api/store";

export const ImportTemplateDetailsStep = ({ next, cancel, startOver, importTemplateDetails }
  : { next?, cancel?, startOver?, importTemplateDetails?: ImportTemplateDetails }) => {

  const assetTypeOptionMultiDefault = [{ id: AssetType.invoice, name: 'Invoice', disabled: false }, { id: AssetType.creditNote, name: 'Credit Note', disabled: false }, { id: AssetType.unappliedCash, name: 'Unapplied Cash', disabled: false }];
  const Model = attributes({
    name: type(String).required,
    assetType: type(Number).required,
    isDefault: type(Boolean),
    isActive: type(Boolean),
    isStandart: type(Boolean)
  });
  const state = useModel(Model);
  const assetTypeOptionSingle = globalStore.assetTypesLookup.items.models.some(item => item.id === 10)
    ? globalStore.assetTypesLookup.items.models.filter(item => item.id !== 10)
    : globalStore.assetTypesLookup.items.models;
  const assetTypeOptionMulti = useLinked(assetTypeOptionMultiDefault);
 const isEdit = () => importTemplateDetails.id;
  const isStandard = () => importTemplateDetails.isStandard;
  const getNextButtonText = () => isEdit() ? 'Next: Map Fields' : 'Next: Upload Sample Files';
  const isDisabledNextStep = () => !(state.name && checkAssetTypesValid($assetType.value) && checkCompaniesValid($companies.value));
  const isDisabledStartOverButton = () => !(state.name || checkDataValid($assetType.value) || checkCompaniesValid($companies.value));
  const [isMultiple, setMultipleType] = useState<boolean>(isEdit && importTemplateDetails.assetTypes.length > 1);
  const $companies = useLinked([]);
  const $assetType = useLinked([]).onChange(values => {
    if (isMultiple) {
      if (values && values.find(x => x.id === AssetType.creditNote)) {
        assetTypeOptionMulti.value.find(x => x.id == AssetType.unappliedCash).disabled = true;
        assetTypeOptionMulti.set(assetTypeOptionMulti.value);
      }
      if (values && values.find(x => x.id === AssetType.unappliedCash)) {
        assetTypeOptionMulti.value.find(x => x.id == AssetType.creditNote).disabled = true;
        assetTypeOptionMulti.set(assetTypeOptionMulti.value);
      }
      if (!values || (values.length == 1 && values.find(x => x.id === AssetType.invoice))) {
        assetTypeOptionMulti.set(assetTypeOptionMultiDefault);
      }
    } else {
      importTemplateDetails.assetFilterCriteria = {} as AssetFilterCriteria;
    }
  });

  useEffect(() => {
    state.name = importTemplateDetails.name;
    state.isDefault = importTemplateDetails.isDefault;
    state.isActive = importTemplateDetails.isActive;
    $assetType.set(importTemplateDetails.assetTypes);
    setMultipleType(importTemplateDetails.assetTypes.length > 1);
    $companies.set(importTemplateDetails.companies)
  }, [importTemplateDetails.name,
  importTemplateDetails.assetTypes,
  importTemplateDetails.companies]);

  const checkAssetTypesValid = (data) => {
    if (isMultiple && Array.isArray(data)) {
      return data.length > 1; //for multiple case assetTypes should be more than 1
    }
    return checkDataValid(data);
  }

  const checkCompaniesValid = (data) => {
    if (isMultiple && Array.isArray(data)) {
      return data.length > 0;
    }
    return checkDataValid(data);
  }

  const checkDataValid = (data) => {
    if (data === null) {
      return false;
    }
    return data.length !== 0;
  }

  const handleChangeMultipleType = (status) => {
    setMultipleType(status);
    $assetType.set(null)
  }

  const handleAssetType = (data) => {
    if (isMultiple) {
      return data.value && data.value.length && data.value
    }
    return Array.isArray(data.value) ? data.value : [data.value]
  }

  return (
    <>
      <WorkflowTitleStyled>
        Step 1: Name your Import Template and fill out additional details
      </WorkflowTitleStyled>
      <ModelDetailsContainer>
        <WorkflowBody>
          <WorkflowTitleStyled>
            Create a name for your import template
          </WorkflowTitleStyled>
          <Section column>
            <FilterLabel>Import Template Name</FilterLabel>
            <Flex row>
              <Flex column>
                <ErrorHighlighter classes={makeErrorHighlighterStyles()}>
                  <Input $value={state.$.name} placeholder="Template Name" />
                </ErrorHighlighter>
              </Flex>
            </Flex>
          </Section>
          <Flex column>
            <WorkflowTitleWrapper>
              <WorkflowTitleStyled>
                Select the Asset type for your Import Template
              </WorkflowTitleStyled>
            </WorkflowTitleWrapper>
          </Flex>
          <Section row>
            <SwitchButton value={isMultiple} change={handleChangeMultipleType} />
            <DropdownBox>
              <FilterLabel>Asset Type</FilterLabel>
              <ErrorHighlighter>
                <DigitizationSelect
                  placeholder={'Asset Type'}
                  $value={$assetType}
                  options={isMultiple ? assetTypeOptionMulti.value : assetTypeOptionSingle}
                  isMulti={isMultiple}
                />
              </ErrorHighlighter>
            </DropdownBox>
            {isEdit() && importTemplateDetails.delimiter &&
              <Flex row>
                <Flex column>
                  <FilterLabel>Delimiter</FilterLabel>
                  <StyledInput data-qa-id="delimiter" value={importTemplateDetails.delimiter} disabled />
                </Flex>
              </Flex>
            }
          </Section>
          <Flex row>
            <WorkflowTitleWrapper>
              <WorkflowTitleStyled>
                Select the Companies that can use this Import Template
              </WorkflowTitleStyled>
            </WorkflowTitleWrapper>
          </Flex>
          {isStandard() ? <></> : <>
            <Section row>
              <DropdownBox>
                <FilterLabel>Companies</FilterLabel>
                <ErrorHighlighter>
                  <DigitizationSelect
                    placeholder={'Companies'}
                    $value={$companies}
                    getItems={getCompanies}
                    isMulti
                  />
                </ErrorHighlighter>
              </DropdownBox>
            </Section>
          </>}
        </WorkflowBody>
        <DefaultTemplateContainer>
          {isStandard() ? <></> : <StyledFormControlLabel
            disabled={!state.$.isActive.value && Boolean(isEdit())}
            control={
              <Switch
                {...state.$.isDefault.props}
                color="primary"
              />
            }
            label={"Default template"}
            labelPlacement="start"
          />}
        </DefaultTemplateContainer>
      </ModelDetailsContainer>
      <WorkflowFooter>
        <Columns>
          <Column>
            <Flex row>
              <CancelButton color="primary" onClick={cancel}>Cancel</CancelButton>
              {isEdit() ? <></> : <StartOverButton color="primary" onClick={startOver} disabled={isDisabledStartOverButton()}>Start Over</StartOverButton>}
            </Flex>
          </Column>
          <Column>
            <RightAlignedButtons>
              <ConfirmationButton disabled={isDisabledNextStep()}
                onClick={() => next({ id: importTemplateDetails.id, name: state.name, assetTypes: handleAssetType($assetType), companies: $companies.value, isDefault: state.isDefault })}
              >{getNextButtonText()}</ConfirmationButton>
            </RightAlignedButtons>
          </Column>
        </Columns>
      </WorkflowFooter>
    </>
  );
};

const StyledFormControlLabel = withStyles({
  label: {
    fontSize: '13px',
    fontFamily: 'Montserrat',
    fontWeight: 500,
    color: 'grey'
  }
})(FormControlLabel)

const DropdownBox = styled(Column)({
  marginLeft: 0,
  marginRight: 0,
});

const FilterLabel = div({
  fontFamily: 'Montserrat',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '13px',
  padding: '4px',
  marginTop: '5px',
  color: 'grey'
});

const ModelDetailsContainer = div({
  background: '#FFFFFF',
  border: '1px solid #E2F0F7',
  borderRadius: '4px',
  padding: '30px',
  marginBottom: '50px',
  display: 'flex',
  justifyContent: 'space-between'
});

const DefaultTemplateContainer = div({
  marginTop: '20px',
  flex: '0 0 200px',
});

const Section = styled(Flex)({
  paddingLeft: '30px',
  display: 'flex',
  flexDirection: 'column',
})

const WorkflowTitleWrapper = styled('div')({
  margin: '30px 0 0 0'
})

const WorkflowTitleStyled = styled(WorkflowTitle)({
  height: 30,
  fontSize: '14px',
})

const StyledInput = styled(Input)({
  height: 32,
  border: "1px solid #E3E9ED !important",
  color: 'rgba(0, 0, 0, 0.38)',
  background: '#F8F8F8'
})

const makeErrorHighlighterStyles = makeStyles({
  root: {
    '& [class*="-error-"]': {
      border: '1px solid #FF274B !important',
      borderRadius: '1px'
    }
  },
});